"use client";

import PrimaryBtn from "@/components/PrimaryBtn";
import { Images, Icons } from "@assets/assets";
import React, { useState } from "react";
import { saveNewsLetter } from "@/lib/ApiClient";
import Image from "next/image";
import { useIsMobile } from "hooks/useMediaQuery";
interface CtaProps {
  bg?: string;
  bgImage?: any;
  title1?: React.ReactNode;
  title2?: React.ReactNode;
  description?: React.ReactNode;
  button?: React.ReactNode;
  direction?: "forward" | "backward";
  animationShade?: string;
  circleColor?: string;
}

export default function Cta({
  bg = "bg-[#fff]",
  bgImage,
  title1,
  title2,
  description,
  button,
  direction = "forward",
  animationShade = "bg-gradient-to-t from-white via-white/70 to-transparent",
  circleColor = "border-[#b9c6df]",
}: CtaProps) {
  const isMobile = useIsMobile();
  if (isMobile === true) return null;
  return (
    <section
      className={`cta ${bg} overflow-hidden`}
      style={
        bgImage
          ? {
              backgroundImage: `url(${bgImage.src || bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] py-[40px] lg:py-[150px] relative z-1">
        <div className="self-stretch text-center justify-center flex flex-col items-center gap-[14px] relative z-2">
          {/* backward → description first */}
          {direction === "backward" && (
            <p className="w-full max-w-full text-center text-[#636363] text-[length:var(--hero-para)] font-normal leading-[150%]">
              {description}
            </p>
          )}

          <h2 className="lg:block leading-[100%] max-w-[40ch] xl:max-w-[100%]  text-[length:var(--section-title)] ">
            <span className="text-[#00040b] font-medium clash-font capitalize leading-[90%]">
              {title1}
            </span>
            <span className="font-medium clash-font capitalize leading-[90%]">
              {title2}
            </span>
          </h2>

          {/* forward → description after title */}
          {direction === "forward" && (
            <p className="w-full max-w-[60ch] xl:mb-[30px] text-center text-[#636363] text-[length:var(--hero-para)] font-normal leading-[150%]">
              {description}
            </p>
          )}

          {button}
        </div>

        {/* circle animation here */}
        <div
          className="pointer-events-none absolute inset-0 opacity-50  hidden lg:flex items-center justify-center z-1"
          aria-hidden="true"
        >
          {/* Wrapper sized to the largest circle */}
          <div className="relative h-[80%] aspect-[1/1]">
            {/* Outer static ring */}
            <div
              className={`absolute inset-0 rounded-full border ${circleColor}`}
            ></div>
            {/* Outer rotating gradient half-box (masked by the circle) */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute inset-0 origin-center animate-[spin_4.5s_linear_infinite]">
                <div
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-1/2 ${animationShade}`}
                ></div>
              </div>
            </div>

            {/* Inner static ring */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[60%] aspect-[1/1]">
              <div
                className={`absolute inset-0 rounded-full border ${circleColor}`}
              ></div>
              {/* Inner rotating gradient half-box */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div className="absolute inset-0 origin-center animate-[spin_4.5s_linear_infinite]">
                  <div
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-1/2 ${animationShade}`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// New: SubscribeCTA — newsletter banner styled like Cta (used on Blog pages)
interface SubscribeCTAProps {
  bg?: string;
  overline?: React.ReactNode;
  title1?: React.ReactNode;
  title2?: React.ReactNode;
  placeholder?: string;
  buttonText?: React.ReactNode;
  direction?: "forward" | "backward";
  animationShade?: string;
  circleColor?: string;
}

export function SubscribeCTA({
  bg = "bg-[#0b41b3]",
  overline = "SUBSCRIBE TO OUR BLOGS",
  title1 = "join Our Newsletter to get",
  title2 = "monthly insights and updates",
  placeholder = "Input you email",
  buttonText = "Subscribe Now",
  animationShade = "bg-gradient-to-t from-[#0C41B2] via-[#0C41B2]/70 to-transparent",
  circleColor = "border-[rgba(255,255,255,0.2)]",
}: SubscribeCTAProps) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const handleSubmit = async () => {
    // Basic email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setMessage("Email is required.");
      setMessageType("error");
      return;
    }

    if (!emailRegex.test(email)) {
      setMessage("Please enter a valid email address.");
      setMessageType("error");
      return;
    }

    const data = await saveNewsLetter({ email: email });

    if (data?.success) {
      setEmail("");
      setMessage(data?.message);
      setMessageType("success");
      setTimeout(() => {
        setMessage("");
      }, 2000);
    } else {
      setMessage(data?.message);
      setMessageType("error");
    }
  };
  const isMobile = useIsMobile();
  if (isMobile === true) return null;
  return (
    <section className={`subscribe-cta ${bg} overflow-hidden`}>
      <div className="absolute inset-0 z-0">
        <Image
          src={Images?.serviceCtaBg.src}
          alt="texture"
          fill
          className="object-cover mix-blend-overlay opacity-60"
          sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
        />
      </div>
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] py-[150px] relative z-1">
        <div className="self-stretch text-center justify-center flex flex-col items-center gap-[10px] relative z-2">
          <p className="text-white uppercase tracking-tight text-[length:var(--primarybtn)]">
            {overline}
          </p>
          <h2 className="leading-[100%] max-w-[28ch] xl:max-w-[100%] lg:block text-[length:var(--service-banner-title)] text-white">
            <span className="font-medium clash-font capitalize leading-[90%]">
              {title1}
            </span>{" "}
            <br className="hidden lg:block" />
            <span className="font-medium clash-font capitalize leading-[90%]">
              {" "}
              {title2}
            </span>
          </h2>

          {/* Input and button row */}
          <div className="flex flex-col lg:flex-row items-center gap-4 mt-2 lg:mt-8">
            <div className="flex flex-col gap-2">
              <input
                className="min-w-[300px] w-[540px] max-w-[70vw] rounded-[70px] 
           
            flex items-center text-white h-[var(--primarybtn-height)] font-semibold leading-[100%] whitespace-nowrap transition-colors lg:px-10 px-5  border bg-white/5 outline-white/10 border-white/20"
                placeholder={placeholder}
                type="email"
                name="email"
                id="email"
                onChange={(e: any) => {
                  setEmail(e.target.value);
                }}
              />
              {message && (
                <p
                  className={`mt-3 text-sm ${
                    messageType === "error" ? "text-red-400" : "text-green-400"
                  }`}
                >
                  {message}
                </p>
              )}
            </div>

            <PrimaryBtn
              text={buttonText}
              iconSrc={Icons?.chevronBlue}
              dark={true}
              onClick={handleSubmit}
            />
          </div>
        </div>

        {/* circle animation like reference */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center z-1"
          aria-hidden="true"
        >
          <div className="relative h-[80%] aspect-[1/1]">
            <div
              className={`absolute inset-0 rounded-full border ${circleColor}`}
            ></div>
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute inset-0 origin-center animate-[spin_4.5s_linear_infinite]">
                <div
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-1/2 ${animationShade}`}
                ></div>
              </div>
            </div>

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[60%] aspect-[1/1]">
              <div
                className={`absolute inset-0 rounded-full border ${circleColor}`}
              ></div>
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div className="absolute inset-0 origin-center animate-[spin_4.5s_linear_infinite]">
                  <div
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-1/2 ${animationShade}`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
